import { useCallback, useEffect, useState, Fragment } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { adminService } from "../../features/admin/service";
import type { AiSearchUsageItem, AiSearchUsageTotals } from "../../features/admin/types";
import Spinner from "../../components/ui/Spinner";
import RefreshButton from "../../components/admin/RefreshButton";

type DatePreset = "all" | "24h" | "7d" | "30d" | "custom";

const DATE_PRESETS: { key: DatePreset; label: string }[] = [
  { key: "all", label: "All time" },
  { key: "24h", label: "Past 24 hours" },
  { key: "7d", label: "Past 7 days" },
  { key: "30d", label: "Past 30 days" },
  { key: "custom", label: "Custom range" },
];

const PRESET_MS: Record<Exclude<DatePreset, "all" | "custom">, number> = {
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
};

const localToIso = (v: string): string | undefined => (v ? new Date(v).toISOString() : undefined);

const roleColor = (role: string) => {
  if (role === "seller") return "bg-cyan-500/10 text-cyan-400";
  if (role === "super_admin") return "bg-amber-500/10 text-amber-400";
  return "bg-emerald-500/10 text-emerald-400";
};

const nfmt = (n: number) => (n ?? 0).toLocaleString();
const usd = (n: number) => `$${(n ?? 0).toFixed(4)}`;
const inr = (n: number) => `₹${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export type AiSearchFeature = "rag_chat" | "market_research";

const FEATURE_COPY: Record<AiSearchFeature, { title: string; subtitle: string; searchPlaceholder: string; costNote?: string }> = {
  rag_chat: {
    title: "RAG Chat Usage",
    subtitle: "Per-user token usage and cost for the fresh_rag buyer chat agent",
    searchPlaceholder: "Search by user name or email…",
  },
  market_research: {
    title: "Market Research Usage",
    subtitle: "Per-user token usage and cost for the market research assistant",
    searchPlaceholder: "Search by user name or email…",
    costNote: "Cost is estimated from token counts (this feature doesn't store real per-call OpenRouter cost).",
  },
};

/** Shared per-user usage table for the two AI Search features (RAG chat,
 *  market research) — same shape (query count / tokens / cost by model),
 *  different backend source, so one component takes `feature` instead of
 *  duplicating the table twice. */
const AdminAiSearchUsagePage: React.FC<{ feature: AiSearchFeature }> = ({ feature }) => {
  const copy = FEATURE_COPY[feature];

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  const [items, setItems] = useState<AiSearchUsageItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState<AiSearchUsageTotals | null>(null);
  const [inrRate, setInrRate] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  const [datePreset, setDatePreset] = useState<DatePreset>("all");
  const [dateMenuOpen, setDateMenuOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [feature, datePreset, customFrom, customTo]);

  const fetchUsage = useCallback(() => {
    let dateFrom: string | undefined;
    let dateTo: string | undefined;
    if (datePreset === "custom") {
      dateFrom = localToIso(customFrom);
      dateTo = localToIso(customTo);
    } else if (datePreset !== "all") {
      dateFrom = new Date(Date.now() - PRESET_MS[datePreset]).toISOString();
    }

    setLoading(true);
    const fetcher = feature === "rag_chat" ? adminService.getRagChatUsage : adminService.getMarketResearchUsage;
    return fetcher({
      page,
      page_size: pageSize,
      search: debouncedSearch || undefined,
      date_from: dateFrom,
      date_to: dateTo,
    })
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
        setTotals(res.totals);
        setInrRate(res.inr_rate);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [feature, page, pageSize, debouncedSearch, datePreset, customFrom, customTo]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      <AdminPageHeader
        title={copy.title}
        subtitle={
          <>
            {copy.subtitle}
            {inrRate ? ` (USD→INR @ ${inrRate})` : ""}
          </>
        }
        actions={<RefreshButton onRefresh={fetchUsage} />}
      >
        {copy.costNote && <p className="text-amber-500/80 text-xs mt-1">{copy.costNote}</p>}
      </AdminPageHeader>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Users</p>
          <p className="text-2xl font-bold text-white">{nfmt(totals?.user_count ?? 0)}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Queries</p>
          <p className="text-2xl font-bold text-white">{nfmt(totals?.query_count ?? 0)}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Total Tokens</p>
          <p className="text-2xl font-bold text-white">{nfmt(totals?.total_tokens ?? 0)}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Cost (USD)</p>
          <p className="text-2xl font-bold text-emerald-400">{usd(totals?.cost_usd ?? 0)}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Cost (INR)</p>
          <p className="text-2xl font-bold text-amber-400">{inr(totals?.cost_inr ?? 0)}</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={copy.searchPlaceholder}
          className="flex-1 min-w-[220px] max-w-sm bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500/50"
        />

        <div className="relative">
          <button
            type="button"
            onClick={() => setDateMenuOpen((o) => !o)}
            className="flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-300 hover:border-gray-700 transition-colors"
          >
            {DATE_PRESETS.find((p) => p.key === datePreset)?.label ?? "All time"}
            <span className="text-gray-600 text-xs">▾</span>
          </button>
          {dateMenuOpen && (
            <div
              className="absolute right-0 left-auto max-sm:left-1/2 max-sm:right-auto max-sm:-translate-x-1/2 mt-2 w-64 max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto bg-gray-900 border border-gray-800 rounded-lg shadow-xl z-20 p-2"
              onMouseLeave={() => setDateMenuOpen(false)}
            >
              {DATE_PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => {
                    setDatePreset(p.key);
                    if (p.key !== "custom") setDateMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors ${
                    datePreset === p.key ? "bg-indigo-500/20 text-indigo-300" : "text-gray-400 hover:bg-gray-800 hover:text-white"
                  }`}
                >
                  {p.label}
                </button>
              ))}
              {datePreset === "custom" && (
                <div className="mt-2 pt-2 border-t border-gray-800 space-y-2">
                  <label className="block">
                    <span className="text-[10px] text-gray-500 uppercase tracking-wide">From</span>
                    <input
                      type="datetime-local"
                      value={customFrom}
                      onChange={(e) => setCustomFrom(e.target.value)}
                      style={{ colorScheme: "dark" }}
                      className="mt-1 w-full bg-gray-950 border border-gray-800 rounded-md px-2 py-1.5 text-xs text-white focus:outline-none focus:border-indigo-500/50"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] text-gray-500 uppercase tracking-wide">To</span>
                    <input
                      type="datetime-local"
                      value={customTo}
                      onChange={(e) => setCustomTo(e.target.value)}
                      style={{ colorScheme: "dark" }}
                      className="mt-1 w-full bg-gray-950 border border-gray-800 rounded-md px-2 py-1.5 text-xs text-white focus:outline-none focus:border-indigo-500/50"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => setDateMenuOpen(false)}
                    className="w-full mt-1 px-3 py-1.5 rounded-md text-xs font-medium bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30 transition-colors"
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-4 text-gray-500 font-medium">User</th>
                <th className="text-right py-3 px-4 text-gray-500 font-medium">Queries</th>
                <th className="text-right py-3 px-4 text-gray-500 font-medium">Tokens</th>
                <th className="text-right py-3 px-4 text-gray-500 font-medium">Cost (USD)</th>
                <th className="text-right py-3 px-4 text-gray-500 font-medium">Cost (INR)</th>
                <th className="py-3 px-4" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="py-16 text-center text-gray-500"><Spinner /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} className="py-16 text-center text-gray-500">No usage found</td></tr>
              ) : (
                items.map((it) => (
                  <Fragment key={it.user.id}>
                    <tr className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400 font-semibold text-xs flex-shrink-0">
                            {it.user.name?.[0]?.toUpperCase() || "?"}
                          </div>
                          <div className="min-w-0">
                            <p className="text-white font-medium text-sm truncate max-w-[200px]">{it.user.name || "—"}</p>
                            <p className="text-gray-500 text-xs truncate max-w-[200px]">{it.user.email}</p>
                          </div>
                          {it.user.role && (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${roleColor(it.user.role)}`}>
                              {it.user.role}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right text-white whitespace-nowrap">{nfmt(it.query_count)}</td>
                      <td className="py-3 px-4 text-right text-gray-300 whitespace-nowrap">
                        <span className="text-white">{nfmt(it.total_tokens)}</span>
                        <span className="text-gray-600 text-xs block">{nfmt(it.prompt_tokens)}p / {nfmt(it.completion_tokens)}c</span>
                      </td>
                      <td className="py-3 px-4 text-right text-emerald-400 whitespace-nowrap">{usd(it.cost_usd)}</td>
                      <td className="py-3 px-4 text-right text-amber-400 whitespace-nowrap">{inr(it.cost_inr)}</td>
                      <td className="py-3 px-4 text-right">
                        {it.models.length > 0 && (
                          <button
                            onClick={() => setExpanded(expanded === it.user.id ? null : it.user.id)}
                            className="text-xs text-indigo-400 hover:text-indigo-300"
                          >
                            {expanded === it.user.id ? "Hide" : `${it.models.length} model${it.models.length > 1 ? "s" : ""}`}
                          </button>
                        )}
                      </td>
                    </tr>
                    {expanded === it.user.id && (
                      <tr className="bg-gray-950/60">
                        <td colSpan={6} className="px-4 py-3">
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-gray-600">
                                  <th className="text-left py-1 px-2 font-medium">Model</th>
                                  <th className="text-right py-1 px-2 font-medium">Calls</th>
                                  <th className="text-right py-1 px-2 font-medium">Prompt</th>
                                  <th className="text-right py-1 px-2 font-medium">Completion</th>
                                  <th className="text-right py-1 px-2 font-medium">Total</th>
                                  <th className="text-right py-1 px-2 font-medium">Cost (USD)</th>
                                  <th className="text-right py-1 px-2 font-medium">Cost (INR)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {it.models.map((m, i) => (
                                  <tr key={i} className="text-gray-400 border-t border-gray-800/50">
                                    <td className="py-1 px-2 truncate max-w-[240px]">{m.model}</td>
                                    <td className="py-1 px-2 text-right">{nfmt(m.calls)}</td>
                                    <td className="py-1 px-2 text-right">{nfmt(m.prompt_tokens)}</td>
                                    <td className="py-1 px-2 text-right">{nfmt(m.completion_tokens)}</td>
                                    <td className="py-1 px-2 text-right">{nfmt(m.total_tokens)}</td>
                                    <td className="py-1 px-2 text-right text-emerald-400">{usd(m.cost_usd)}</td>
                                    <td className="py-1 px-2 text-right text-amber-400">{inr(m.cost_inr)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-5 py-3 border-t border-gray-800 flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {total > 0 ? `Showing page ${page} of ${totalPages} · ${nfmt(total)} total` : "No results"}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="px-3 py-1.5 rounded-lg text-sm bg-gray-800 text-gray-300 disabled:opacity-40 hover:bg-gray-700 transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className="px-3 py-1.5 rounded-lg text-sm bg-gray-800 text-gray-300 disabled:opacity-40 hover:bg-gray-700 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAiSearchUsagePage;
